/**
 * EC scroll animations: elements with [data-ec-animate] fade in (up, or a
 * slight zoom for "zoom") the first time 15% of them is on screen, once.
 *
 * - Hidden state only exists while <html> has .ec-anim, which the head snippet
 *   (snippets/ec-animations.liquid) adds; without JS everything stays visible.
 * - [data-ec-animate-group] (slider tracks, rows, footer grid): its
 *   [data-ec-animate] children reveal together when the group comes in, so
 *   slides scrolled sideways out of view are not left hidden.
 * - Items revealed together are staggered by 70ms, max 6 steps, then together.
 * - When an item is done the attribute is removed so its own hover
 *   transitions/transforms work again.
 */
(() => {
  const root = document.documentElement;
  window.ecAnimationsReady = true;
  if (!root.classList.contains('ec-anim')) return;

  const STAGGER = 70;
  const MAX_STEPS = 6;
  const THRESHOLD = 0.15;
  const seen = new WeakSet();
  const openGroups = new WeakSet();

  const finish = (el) => {
    el.removeAttribute('data-ec-animate');
    el.classList.remove('ec-in');
    el.style.removeProperty('--ec-anim-delay');
  };

  const reveal = (items) => {
    items.forEach((el, i) => {
      if (!el.hasAttribute('data-ec-animate') || el.classList.contains('ec-in')) return;
      const delay = Math.min(i, MAX_STEPS - 1) * STAGGER;
      el.style.setProperty('--ec-anim-delay', `${delay}ms`);
      el.classList.add('ec-in');
      const duration = parseFloat(getComputedStyle(el).transitionDuration) * 1000 || 700;
      const done = (event) => {
        if (event && event.target !== el) return;
        el.removeEventListener('transitionend', done);
        clearTimeout(timer);
        finish(el);
      };
      const timer = setTimeout(done, duration + delay + 150);
      el.addEventListener('transitionend', done);
    });
  };

  const byDomOrder = (a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);

  // 15% of the element, or 15% of the viewport for elements taller than the screen
  const isIn = (entry) =>
    entry.isIntersecting &&
    (entry.intersectionRatio >= THRESHOLD || entry.intersectionRect.height >= window.innerHeight * THRESHOLD);

  const observer = new IntersectionObserver(
    (entries) => {
      const batch = [];
      entries.forEach((entry) => {
        if (!isIn(entry)) return;
        observer.unobserve(entry.target);
        batch.push(entry.target);
      });
      batch.sort(byDomOrder);

      const singles = [];
      batch.forEach((el) => {
        if (el.hasAttribute('data-ec-animate-group')) {
          openGroups.add(el);
          reveal(groupItems(el));
          if (el.hasAttribute('data-ec-animate')) singles.push(el);
        } else {
          singles.push(el);
        }
      });
      reveal(singles);
    },
    { threshold: [0, THRESHOLD, 0.5, 1] }
  );

  const groupItems = (group) =>
    Array.from(group.querySelectorAll('[data-ec-animate]')).filter(
      (el) => el.closest('[data-ec-animate-group]') === group
    );

  const scan = (scope) => {
    const nodes = [];
    if (scope.matches?.('[data-ec-animate], [data-ec-animate-group]')) nodes.push(scope);
    scope.querySelectorAll?.('[data-ec-animate], [data-ec-animate-group]').forEach((el) => nodes.push(el));
    nodes.forEach((el) => {
      if (seen.has(el)) return;
      // items inside a group are revealed by the group, not observed on their own
      const group = el.parentElement?.closest('[data-ec-animate-group]');
      if (group && !el.hasAttribute('data-ec-animate-group')) {
        // added later to a group that is already in: show it right away
        if (openGroups.has(group)) reveal([el]);
        return;
      }
      seen.add(el);
      observer.observe(el);
    });
  };

  const start = () => {
    scan(document.body);
    // product grids re-rendered by filters, recommendations loaded later, etc.
    new MutationObserver((mutations) => {
      mutations.forEach((m) => m.addedNodes.forEach((node) => node.nodeType === 1 && scan(node)));
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
